import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { usePointsIntegration } from '@/hooks/points/usePointsIntegration';

export const useProfileVerificationStatus = () => {
  const { createPointsAccountForVerification } = usePointsIntegration();

  const updateProfileVerificationStatus = async (
    userId: string, 
    status: 'verified' | 'rejected',
    notes?: string
  ) => {
    try {
      // Get current admin user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Authentication required');
      }

      console.log(`Updating user ${userId} verification status to ${status} by ${user.id}`);

      // Update profile verification status directly
      if (status === 'verified') {
        // If verified, update both verification status and user role to pollster
        const { error: profileError } = await supabase
          .from('profiles')
          .update({
            id_verification_status: status,
            role: 'pollster',
            verified: true,
            updated_at: new Date().toISOString()
          })
          .eq('id', userId);

        if (profileError) {
          console.error('Profile update error:', profileError);
          throw profileError;
        }

        // Create country verification records for verified documents
        try {
          const { data: verifiedDocs } = await supabase
            .from('user_id_documents')
            .select('verification_country, constituency_code')
            .eq('user_id', userId)
            .eq('verification_status', 'verified')
            .not('verification_country', 'is', null);

          if (verifiedDocs && verifiedDocs.length > 0) {
            // Create user country verification records
            const uniqueCountries = [...new Set(verifiedDocs.map(doc => doc.verification_country))];
            for (const countryCode of uniqueCountries) {
              if (countryCode) {
                const docWithCountry = verifiedDocs.find(doc => doc.verification_country === countryCode);
                
                // Insert or update country verification record
                const { error: countryVerificationError } = await supabase
                  .from('user_country_verifications')
                  .upsert({
                    user_id: userId,
                    country_code: countryCode,
                    constituency_code: docWithCountry?.constituency_code,
                    verification_status: 'verified',
                    verified_at: new Date().toISOString(),
                    primary_verification: true
                  }, {
                    onConflict: 'user_id,country_code'
                  });

                if (countryVerificationError) {
                  console.error('Country verification error:', countryVerificationError);
                }

                // Create points account for this country
                await createPointsAccountForVerification(userId, countryCode);
              }
            }
          }
        } catch (countryError) {
          console.error('Error creating country verification records:', countryError);
          // Don't fail the verification for country record creation errors
        }
      } else {
        // If not verified, only update verification status
        const { error: profileError } = await supabase
          .from('profiles')
          .update({
            id_verification_status: status,
            updated_at: new Date().toISOString()
          })
          .eq('id', userId);

        if (profileError) {
          console.error('Profile update error:', profileError);
          throw profileError;
        }
      }

      // Log admin activity
      try {
        const { error: logError } = await supabase
          .from('admin_activity_logs')
          .insert({
            admin_id: user.id,
            action: 'profile_verification_update',
            target_user_id: userId,
            details: {
              new_status: status,
              notes: notes,
              timestamp: new Date().toISOString()
            }
          });

        if (logError) {
          console.error('Admin activity log error:', logError);
          // Don't fail the process for logging errors
        }
      } catch (logError) {
        console.error('Error logging admin activity:', logError);
      }

      console.log(`User ${userId} verification status updated to ${status} successfully`);

    } catch (error) {
      console.error(`Error updating profile ${status} status:`, error);
      toast.error(`Failed to update user verification status: ${(error as Error).message}`);
      throw error;
    }
  };

  // Create a verification history record for audit purposes
  const createVerificationHistoryRecord = async (
    userId: string,
    documentId: string,
    status: 'verified' | 'rejected',
    verifierId: string,
    notes?: string
  ) => {
    try {
      // Use direct table insert instead of RPC function
      const { error } = await supabase
        .from('verification_history')
        .insert({
          user_id: userId,
          document_id: documentId,
          action: status,
          verified_by: verifierId,
          notes: notes || null,
          created_at: new Date().toISOString()
        });
        
      if (error) throw error;
      
      console.log(`Verification history record created for user ${userId}`);
    } catch (error) {
      console.error('Error creating verification history record:', error);
      // Don't throw here, as this is a supplementary operation
    }
  };

  return { 
    updateProfileVerificationStatus,
    createVerificationHistoryRecord
  };
};
